/**
 * M5-T4 StocktakeReviewPage (issue #616).
 *
 * Variance review before commit. Three groups (positive / negative /
 * zero) highlight non-zero variances. The "Commit" button is gated
 * behind an explicit operator confirmation toggle — closing a
 * stocktake amortises variances into the inventory ledger
 * (`type='stocktake_adjust'`) and is not reversible.
 *
 * For terminal stocktakes (committed / cancelled) the page renders
 * read-only — variance groups still surface, commit / cancel
 * controls are hidden, and a status badge explains the state.
 *
 * Permission gate is route-level (`INGREDIENTS_WRITE` so the commit
 * action is reachable; viewers without write should not see this
 * page).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Equal, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageShell,
  Button,
  Checkbox,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { formatNumber } from '@lib/utils';
import { useStocktakeDetail, useCommitStocktake, useCancelStocktake } from './hooks/useStocktake';
import type { StocktakeLine, StocktakeStatus } from '@/services';

/** Tailwind status badge classes per stocktake status. Mirrors
 *  the `stocktakeResource` list badge helper (features/stocktake). */
function statusClass(status: StocktakeStatus): string {
  switch (status) {
    case 'open':
      return 'bg-primary/10 text-primary';
    case 'counted':
      return 'bg-warning/10 text-foreground';
    case 'committed':
      return 'bg-success/10 text-foreground';
    case 'cancelled':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

interface VarianceGroups {
  positive: StocktakeLine[];
  negative: StocktakeLine[];
  zero: StocktakeLine[];
  uncounted: StocktakeLine[];
}

function groupVariances(lines: StocktakeLine[]): VarianceGroups {
  const out: VarianceGroups = {
    positive: [],
    negative: [],
    zero: [],
    uncounted: [],
  };
  for (const l of lines) {
    if (l.variance_qty === null || l.counted_qty === null) {
      out.uncounted.push(l);
    } else if (l.variance_qty > 0) {
      out.positive.push(l);
    } else if (l.variance_qty < 0) {
      out.negative.push(l);
    } else {
      out.zero.push(l);
    }
  }
  // Sort each group: largest absolute variance first so the operator
  // sees the most impactful adjustments at the top.
  out.positive.sort((a, b) => (b.variance_qty ?? 0) - (a.variance_qty ?? 0));
  out.negative.sort((a, b) => (a.variance_qty ?? 0) - (b.variance_qty ?? 0));
  out.zero.sort((a, b) => a.ingredient_id.localeCompare(b.ingredient_id));
  out.uncounted.sort((a, b) => a.ingredient_id.localeCompare(b.ingredient_id));
  return out;
}

function formatQty(n: number): string {
  return formatNumber(n, { maximumFractionDigits: 3 });
}

export function StocktakeReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const detail = useStocktakeDetail(id);
  const commitMut = useCommitStocktake(id ?? '');
  const cancelMut = useCancelStocktake(id ?? '');

  const [confirmed, setConfirmed] = useState(false);

  const header = detail.data?.stocktake;
  const lines: StocktakeLine[] = detail.data?.lines ?? [];

  const groups = useMemo(() => groupVariances(lines), [lines]);

  if (!id) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('stocktake.review.missingId')}
        </CardContent>
      </Card>
    );
  }
  if (detail.isLoading) return <Loading />;
  if (detail.isError || !header) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('stocktake.review.loadError')}
        </CardContent>
      </Card>
    );
  }

  const isTerminal = header.status === 'committed' || header.status === 'cancelled';
  const canCommit = !isTerminal && (header.status === 'open' || header.status === 'counted');

  const handleCommit = async () => {
    if (!confirmed) {
      toast.error(t('stocktake.review.confirmRequired'));
      return;
    }
    try {
      await commitMut.mutateAsync();
      toast.success(t('stocktake.review.commitSuccess'));
      navigate('/stocktake');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: t('stocktake.review.cancelStocktake'),
      message: t('stocktake.review.cancelConfirm'),
      type: 'danger',
    });
    if (!ok) return;
    try {
      await cancelMut.mutateAsync();
      toast.success(t('stocktake.review.cancelSuccess'));
      navigate('/stocktake');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const renderGroup = (
    titleKey: string,
    icon: React.ReactNode,
    rows: StocktakeLine[],
    accentClass: string,
    testId: string,
  ) => {
    if (rows.length === 0) return null;
    return (
      <Card data-testid={testId}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <span className={accentClass}>{icon}</span>
            {t(titleKey, { count: rows.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                <th className="py-2 px-3">{t('stocktake.review.col.ingredient')}</th>
                <th className="py-2 px-3 text-right">{t('stocktake.review.col.systemQty')}</th>
                <th className="py-2 px-3 text-right">{t('stocktake.review.col.countedQty')}</th>
                <th className="py-2 px-3 text-right">{t('stocktake.review.col.varianceQty')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((line) => (
                <tr
                  key={line.id}
                  className="border-b last:border-0"
                  data-testid={`stocktake-review-row-${line.ingredient_id}`}
                >
                  <td className="py-2 px-3 font-medium">{line.ingredient_id}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {formatQty(line.system_qty)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {line.counted_qty !== null ? formatQty(line.counted_qty) : '—'}
                  </td>
                  <td className={`py-2 px-3 text-right tabular-nums font-semibold ${accentClass}`}>
                    {line.variance_qty !== null ? formatQty(line.variance_qty) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  };

  return (
    <PageShell
      data-testid="stocktake-review-page"
      title={t('stocktake.review.title', { week: header.week_start })}
      subtitle={
        <span className="flex items-center gap-2">
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusClass(header.status)}`}
          >
            {t(`stocktake.status.${header.status}`)}
          </span>
          <span>
            {t('stocktake.review.subtitle', {
              positive: groups.positive.length,
              negative: groups.negative.length,
              zero: groups.zero.length,
              uncounted: groups.uncounted.length,
            })}
          </span>
        </span>
      }
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-12"
            onClick={() => navigate('/stocktake')}
            aria-label={t('stocktake.review.back')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {!isTerminal && (
            <Button
              variant="outline"
              className="h-12"
              onClick={() => navigate(`/stocktake/${encodeURIComponent(id)}/count`)}
              data-testid="stocktake-back-to-count"
            >
              {t('stocktake.review.backToCount')}
            </Button>
          )}
        </>
      }
    >
      {renderGroup(
        'stocktake.review.positiveGroup',
        <ArrowUp className="w-4 h-4" />,
        groups.positive,
        'text-success',
        'stocktake-review-group-positive',
      )}
      {renderGroup(
        'stocktake.review.negativeGroup',
        <ArrowDown className="w-4 h-4" />,
        groups.negative,
        'text-destructive',
        'stocktake-review-group-negative',
      )}
      {renderGroup(
        'stocktake.review.zeroGroup',
        <Equal className="w-4 h-4" />,
        groups.zero,
        'text-muted-foreground',
        'stocktake-review-group-zero',
      )}
      {renderGroup(
        'stocktake.review.uncountedGroup',
        <AlertTriangle className="w-4 h-4" />,
        groups.uncounted,
        'text-warning',
        'stocktake-review-group-uncounted',
      )}

      {canCommit && (
        <Card data-testid="stocktake-commit-card">
          <CardHeader>
            <CardTitle className="text-base">{t('stocktake.review.commitTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('stocktake.review.commitWarning', {
                nonZero: groups.positive.length + groups.negative.length,
                uncounted: groups.uncounted.length,
              })}
            </p>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-12">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                aria-label={t('stocktake.review.confirmCheckbox')}
                data-testid="stocktake-commit-confirm"
              />
              <span>{t('stocktake.review.confirmCheckbox')}</span>
            </label>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                className="h-12"
                onClick={handleCancel}
                disabled={cancelMut.isPending}
                data-testid="stocktake-review-cancel"
              >
                <X className="w-4 h-4 mr-2" />
                {t('stocktake.review.cancelStocktake')}
              </Button>
              <Button
                className="h-12 px-8"
                onClick={handleCommit}
                disabled={!confirmed || commitMut.isPending}
                data-testid="stocktake-commit-button"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {t('stocktake.review.commitButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}

export default StocktakeReviewPage;
